import { Injectable } from '@angular/core';
import { CONSTANTS } from '../../Constants/Constants';
import {
  CostReviewAttributes,
  CostReviewBaseAttributes,
  CostReviewSecurityEnUsAttributes,
  CostReviewGeneralConditions,
  CostReviewPlannedCosts,
  ControllerState,
  LaborData,
  Material,
  PrefabPanel,
  SQWebArea,
  SQWebSystem,
  SubSystem,
  SystemComponent,
  Task,
  CustomerInformation,
  SQWebProject,
  MiscMetaData,
  OverTimePWRAdjustment,
  TravelExpenses,
  ProjectVerificationCategory,
  CostReviewSummary,
  CostReviewStatus,
  CollaborationState,
  SQWebController,
  CostReviewDatabaseRecord,
} from '../../models/dtos';
import { CostReviewType, CULTURE_ID, CostReviewPlannedCostItemName, ProjectTabs, SkillLevels, SubSegmentID } from '../../models/enum';
import { EstimateTypes } from '../../models/dtos/Enums/EstimateTypes';
import { CommonDataService } from '../common-data.service';
import { CommonFunctionsService } from '../common-functions.service';
import { EstimateCommonFunctionsService } from '../estimate/estimate-common-functions.service';
import { SellAsMinimumMarginService } from '../sell-as-minimum-margin.service';
import { SolEstimateService } from '../estimate/sol-estimate.service';
import { CommonMetaDataService } from '../common-meta-data.service';
import { TranslateService } from '@ngx-translate/core';
import { IndexDBService } from '../common/indexDB.service';


/** Shared, read-only context threaded through the project-hierarchy traversal. */
interface IHierarchyIterationContext {
  readonly defaultEngOffice: number;
  readonly energySalesOffices: ReadonlySet<string>;
  readonly energyParts: ReadonlySet<string>;
  readonly isPrefabAvailable: boolean;
}

interface IPlannedCostCollections {
  readonly withLabors: CostReviewPlannedCosts[];
  readonly withoutLabors: CostReviewPlannedCosts[];
}

@Injectable({ 
  providedIn: 'root'
 })
export class CostReviewStateSyncService {
  private isServiceProject = false;

  // Accumulators for the project-hierarchy summary sync (ported from CommonFunctions.IterateProjectHierarchy).
  private graphicsTaskValue = CONSTANTS.ZERO;
  private cdpPartsQuantity = CONSTANTS.ZERO;
  private omCostReviewPartsQuantity = CONSTANTS.ZERO;
  private isEnergyOMCostReviewRequiredForSO = false;
  private isPrefabPanelApplied = false;
  private isPrefabPanelNotApplied = false;
  private isOvertimeEstimatedForProject = false;
  private sqWebProject: SQWebProject | undefined;
  private miscMetaData: MiscMetaData | undefined;
  private readonly CPM_THRESHOLD = 1000000;
  private readonly FIFTY_K = 50000;
  private readonly ONEFIFTY_K = 150000;
  private business: string | undefined;
  private readonly plannedCostLaborMappings: {
    itemName: CostReviewPlannedCostItemName;
    laborFormKey: string;
    estimateTabKey: string;
  }[] = [
    { itemName: CostReviewPlannedCostItemName.projectManagement, laborFormKey: 'projectManagement', estimateTabKey: 'projectManagementLaborHour' },
    { itemName: CostReviewPlannedCostItemName.engineering, laborFormKey: 'engineering', estimateTabKey: 'engineeringLaborHour' },
    { itemName: CostReviewPlannedCostItemName.networkEngineering, laborFormKey: 'networkEngineering', estimateTabKey: 'networkEngineeringLaborHour' },
    { itemName: CostReviewPlannedCostItemName.qualificationEngineering, laborFormKey: 'qualificationEngineering', estimateTabKey: 'qualificationEngineeringLaborHour' },
    { itemName: CostReviewPlannedCostItemName.cyberSecurityEngineering, laborFormKey: 'cyberSecurityEngineering', estimateTabKey: 'cyberSecEngineeringLaborHour' },
    { itemName: CostReviewPlannedCostItemName.energyEngineering, laborFormKey: 'energyEngineering', estimateTabKey: 'energyEngineeringLaborHour' },
    { itemName: CostReviewPlannedCostItemName.electricalEngineering, laborFormKey: 'electricalEngineering', estimateTabKey: 'lEngineeringLaborHour' },
    { itemName: CostReviewPlannedCostItemName.graphics, laborFormKey: 'graphics', estimateTabKey: 'graphicsLaborHour' },
    { itemName: CostReviewPlannedCostItemName.programming, laborFormKey: 'programming', estimateTabKey: 'programmingLaborHour' },
    { itemName: CostReviewPlannedCostItemName.specialist, laborFormKey: 'specialist', estimateTabKey: 'specialistLaborHour' },
    { itemName: CostReviewPlannedCostItemName.digitalDeploymentSpecialist, laborFormKey: 'digitalDeploymentSpecialist', estimateTabKey: 'digitalDeploymentLaborHour' },
    { itemName: CostReviewPlannedCostItemName.mechanical, laborFormKey: 'mechanical', estimateTabKey: 'mechanicalLaborHour' },
    { itemName: CostReviewPlannedCostItemName.electrical, laborFormKey: 'electrical', estimateTabKey: 'electricianLaborHour' },
  ];

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly solEstimateService: SolEstimateService,
    private readonly indexedDbService: IndexDBService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly estimateCommonFunctionsService: EstimateCommonFunctionsService,
    private readonly sellAsMinimumMarginService: SellAsMinimumMarginService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly translate: TranslateService,
  ) { }

  //Entry point
  async loadCostReviewDetails(): Promise<void> {
    const costReviewData = this.commonDataService.getCostReviewData();
    const reviewStatus = costReviewData?.status;
    const collaborationState = this.commonDataService.getCollaborationState();

    if (this.shouldSkipCostReviewDetailsLoading(reviewStatus, collaborationState)
    ) {
      return;
    }

    const reviewObject = costReviewData?.reviewObject;
    if (!reviewObject) {
      return;
    }
    this.initializeProperties();
    await this.updateSummarySection(reviewObject);
    this.initializePlannedCost(reviewObject);
    this.updatePrefabControllerVisibility(costReviewData);
    this.updateMarginDetails(reviewObject);
    this.updateGeneralConditions(reviewObject);
  }

  //Do not load details from project object if Cost Review Status is in-process/pending/approved or if collaborative estimate is in readonly/locked state.
  private shouldSkipCostReviewDetailsLoading(reviewStatus: number, collaborationState: CollaborationState) {
    return !reviewStatus ||
      reviewStatus === CostReviewStatus.InProcess ||
      reviewStatus === CostReviewStatus.Approved ||
      reviewStatus === CostReviewStatus.Pending ||
      collaborationState === CollaborationState.Locked ||
      collaborationState === CollaborationState.Open;
  }

  private initializeProperties() {
    this.business = this.commonDataService.getBusiness();
    this.sqWebProject = this.commonDataService.getEstimateData().projectObject?.sqWebProject;
    this.miscMetaData = this.commonDataService.getMiscMetaData();
  }

  private convertBoolToYesNo(value: boolean): string {
    return value ? this.translate.instant('YES') : this.translate.instant('NO');
  }

  private getSkillLevelName(skillLevel: number | null | undefined): string {
    return skillLevel == null
      ? CONSTANTS.EMPTY_STRING
      : SkillLevels[skillLevel as SkillLevels] ?? CONSTANTS.EMPTY_STRING;
  }

  private updateGeneralConditions(reviewObject: CostReviewBaseAttributes) {
    if (this.business === CONSTANTS.BUSINESS_SECURITY) {
      const securityGeneralConditions = (reviewObject as CostReviewSecurityEnUsAttributes).costReviewSecurityEnUsGeneralConditions;
      this.syncSharedGeneralConditions(securityGeneralConditions);
    }
    else if (this.business !== CONSTANTS.BUSINESS_ELECTRICAL) {
      const generalConditions = reviewObject.costReviewGeneralConditions;
      this.syncSharedGeneralConditions(generalConditions);
      generalConditions.parkingFees = this.getparkingFees();
    }
    reviewObject.isCpmVisible = this.commonDataService.getEstimateMetaData()?.estimateTab.soldASSellPrice > this.CPM_THRESHOLD;
  }

  private syncSharedGeneralConditions(generalConditions: Pick<CostReviewGeneralConditions, 'travelExpensesPlanned' | 'escalationIncluded' | 'overtimeEstimated'>): void {
    if (generalConditions) {
      const escalationRates = this.commonDataService.getEstimateMetaData()?.escalationRates;
      generalConditions.travelExpensesPlanned = this.commonDataService.getEstimateMetaData()?.estimateTab?.travelExpenseCost ?? CONSTANTS.ZERO;
      generalConditions.escalationIncluded = this.convertBoolToYesNo(
        !!escalationRates && (
          this.estimateCommonFunctionsService.isMaterialEscalationEnabled(escalationRates) ||
          this.estimateCommonFunctionsService.isLaborEscalationEnabled(escalationRates)
        )
      );
      generalConditions.overtimeEstimated = this.convertBoolToYesNo(this.isOvertimeEstimatedForProject);
    }
  }

  private hasOvertimeApplied(laborData: LaborData | undefined, includeDxrAdjustments: boolean): boolean {
    if (!laborData?.stdSalesOfficesOtPwrAdjustmentsCollection?.some(office =>
      office.overTimePWRAdjustments?.some(adjustment => this.hasNonStraightOvertime(adjustment))
    )) {
      if (!includeDxrAdjustments) {
        return false;
      }
    } else {
      return true;
    }

    return laborData.stdSalesOfficesOtPwrAdjustmentsDXRCollection?.some(office =>
      office.overTimePWRAdjustments?.some(adjustment => this.hasNonStraightOvertime(adjustment))
    ) ?? false;
  }

  private hasNonStraightOvertime(adjustment: OverTimePWRAdjustment): boolean {
    const isStraightReference = adjustment.referenceNumber === this.translate.instant('STRAIGHT');

    return !isStraightReference && (
      (adjustment.engineerLabor ?? CONSTANTS.ZERO) > CONSTANTS.ZERO ||
      (adjustment.specialistLabor ?? CONSTANTS.ZERO) > CONSTANTS.ZERO ||
      (adjustment.projectManagementLabor ?? CONSTANTS.ZERO) > CONSTANTS.ZERO ||
      (adjustment.electricalEngineerLabor ?? CONSTANTS.ZERO) > CONSTANTS.ZERO ||
      (adjustment.mechanicalEngineerLabor ?? CONSTANTS.ZERO) > CONSTANTS.ZERO
    );
  }

  private getparkingFees(): number {
    let totalparkingFees = CONSTANTS.ZERO;
    if(this.miscMetaData?.travelExpensesCollection && this.miscMetaData.travelRates){
      const tec = this.miscMetaData.travelExpensesCollection
      const tr = this.miscMetaData.travelRates;
      const expenseKeys: Array<keyof TravelExpenses> = [
        'pmExpenses',
        'elecExpenses',
        'engExpenses',
        'mechExpenses',
        'specExpenses',
        'nengExpenses',
        'qengExpenses',
        'cengExpenses',
        'eengExpenses',
        'lengExpenses',
      ];

      for (const te of tec) {
        for (const expenseKey of expenseKeys) {
          const expense = te[expenseKey];
          if (expense && typeof expense === 'object' && 'parkingDays' in expense) {
            totalparkingFees += expense.parkingDays * tr.parking;
          }
        }
      }
    }
    return totalparkingFees;
  }

  private async updateSummarySection(reviewObject: CostReviewBaseAttributes): Promise<void> {

    const summaryObject = reviewObject.costReviewSummary;
    const estimateData = this.commonDataService.getEstimateData();
    const estimateMetadata = this.commonDataService.getEstimateMetaData();
    const otherMetaData = this.commonDataService.getOtherMetaData();
    const defaultEngOffice = estimateMetadata.estimateSettings.defaultEngOffice;
    const verticalMarkets = this.commonMetaDataService.getMetaData()?.verticalMarkets
    
    const userPreferences = this.commonDataService.getPreferenceData()?.preferences;
    summaryObject.projectName = estimateData?.estimateName ?? '';
    summaryObject.customerName = otherMetaData?.customerInformation?.customerName ?? '';
    summaryObject.shipToLocation = this.getshipToLocation(otherMetaData.shipToAccount);
    summaryObject.projectCategoryVerification = summaryObject.isUserProjectCategory ? summaryObject.projectCategoryVerification : this.getProjectCategory();
    summaryObject.verticalMarket = verticalMarkets?.find(market => market.id === estimateMetadata.estimateSettings.verticalMarketId)?.description ?? CONSTANTS.EMPTY_STRING;
    if (this.sqWebProject?.areas) {
      await this.syncHierarchySummary(reviewObject, defaultEngOffice);
    }
    summaryObject.laborEfficiency = this.getSkillLevelName(otherMetaData?.systemBasedLaborSettings.pointToPointCheckoutEffId);
    summaryObject.jobsiteRtMileage = summaryObject.jobsiteRtMileage == CONSTANTS.ZERO ?
      this.commonDataService.getMiscMetaData()?.travelExpenses.travelRates.milageRT :
      summaryObject.jobsiteRtMileage;

    summaryObject.isCollaborative = this.convertBoolToYesNo(this.commonDataService.isCollaborationEstimate());
    summaryObject.isEnergyOMReviewRequiredVisible = this.isOMCostReviewRequiredVisible(userPreferences.cultureString, estimateMetadata.estimateSettings.subSegmentId);
    summaryObject.projectStartDate = this.commonDataService.projectStartDateEndDate.startDate;
    summaryObject.projectEndDate = this.commonDataService.projectStartDateEndDate.endDate;
    summaryObject.projectDuration = this.commonFunctionsService.calculateDurationDays(summaryObject.projectStartDate, summaryObject.projectEndDate);

    if (this.business === CONSTANTS.BUSINESS_SECURITY) {
      summaryObject.opportunityLinked = this.convertBoolToYesNo(!!estimateMetadata.estimateSettings.opportunityId);
      summaryObject.proposalLinked = this.convertBoolToYesNo(!!estimateData.proposalID);
    }

    if (this.business === CONSTANTS.BUSINESS_FIRE) {
      await this.syncFireTaskBasedEstimating(summaryObject);
      this.updateFireJobDetails(summaryObject);
    }
  }

  private updateFireJobDetails(summaryObject: CostReviewSummary) {
    const taskBasedLaborSettings = this.commonDataService.getTaskBasedLaborSettings();
    if (taskBasedLaborSettings) {
      this.commonFunctionsService.mapJobDetailsToSummary(summaryObject, taskBasedLaborSettings);
    }
  }

  private async syncFireTaskBasedEstimating(summaryObject: CostReviewBaseAttributes['costReviewSummary']): Promise<void> {
    if (this.commonDataService.getActiveTab() !== ProjectTabs.Review) {
      return;
    }

    const project = this.sqWebProject;
    if (!project) {
      return;
    }

    const estimateData = this.commonDataService.getEstimateData();
    const cultureId = this.commonDataService.getEstimateCultureId();
    const isNotChangeOrderEstimate = (estimateData?.estimateTypeId ?? CONSTANTS.ONE) !== EstimateTypes.ChangeOrderEstimate;
    const isFireTbeVisible = isNotChangeOrderEstimate && cultureId === CULTURE_ID.EN_US;

    summaryObject.isFireTBEVisibility = isFireTbeVisible;
    if (!isFireTbeVisible) {
      summaryObject.isTBESMApprovalVisible = false;
      return;
    }

    const hasTaskBasedEstimating = await this.hasFireTaskBasedEstimating(project);
    const previousFireTaskBasedEstimating = summaryObject.fireTaskBasedEstimating;
    const yesText = this.convertBoolToYesNo(true);
    const currentFireTaskBasedEstimating = this.convertBoolToYesNo(hasTaskBasedEstimating);

    summaryObject.fireTaskBasedEstimating = currentFireTaskBasedEstimating;

    const approvalWasNotTaken = !summaryObject.isTBEApprovalTaken;
    const answerChanged = previousFireTaskBasedEstimating !== currentFireTaskBasedEstimating;

    if (approvalWasNotTaken || answerChanged) {
      summaryObject.smApprovalRecieved = currentFireTaskBasedEstimating === yesText ? CONSTANTS.ONE : CONSTANTS.TWO;
    }

    summaryObject.isTBESMApprovalVisible =
      summaryObject.isFireTBEVisibility &&
      currentFireTaskBasedEstimating !== yesText;
  }

  private async hasFireTaskBasedEstimating(project: SQWebProject | undefined): Promise<boolean> {
    if (!project?.isItemVisible) {
      return false;
    }

    const projectLaborData = await this.indexedDbService.getLaborDataByGuid(project.itemUniqueId);
    const baseTasks = projectLaborData?.baseTasksCollection ?? [];

    return baseTasks.some(task =>
      task?.referenceNumber === 'T125' ||
      task?.referenceNumber === 'T282'
    );
  }

  private getProjectCategory(): number {
    const sellPrice = this.commonDataService.getEstimateMetaData()?.estimateTab.soldASSellPrice ?? CONSTANTS.ZERO;
    if (sellPrice <= this.FIFTY_K) {
      return ProjectVerificationCategory.W;
    } else if (sellPrice <= this.ONEFIFTY_K) {
      return ProjectVerificationCategory.T;
    } else {
      return ProjectVerificationCategory.S;
    }
  }

  private isOMCostReviewRequiredVisible(cultureString: string, subSegmentId: number): boolean {
    const collectionsServiceProjectUS: readonly SubSegmentID[] = [
      SubSegmentID.AUTOMATION_SERVICE_PROJECT_US,
      SubSegmentID.MECHANICAL_SERVICE_PROJECT_US,
    ];

    const collectionsServiceProjectCN: readonly SubSegmentID[] = [
      SubSegmentID.AUTOMATION_SERVICE_PROJECT_CN_EN,
      SubSegmentID.MECHANICAL_SERVICE_PROJECT_CN_EN,
      SubSegmentID.AUTOMATION_SERVICE_PROJECT_CN_FR,
      SubSegmentID.MECHANICAL_SERVICE_PROJECT_CN_FR,
    ];

    return cultureString === CONSTANTS.LANGUAGE_EN_US
      ? collectionsServiceProjectUS.includes(subSegmentId as SubSegmentID)
      : collectionsServiceProjectCN.includes(subSegmentId as SubSegmentID);
  }

  private async syncHierarchySummary(
    reviewObject: CostReviewBaseAttributes,
    deo: number
  ): Promise<void> {
    const project = this.sqWebProject;
    if (!project?.areas) {
      return;
    }

    // Reset accumulators so a re-sync never carries over values from a previous run.
    this.graphicsTaskValue = CONSTANTS.ZERO;
    this.cdpPartsQuantity = CONSTANTS.ZERO;
    this.omCostReviewPartsQuantity = CONSTANTS.ZERO;
    this.isEnergyOMCostReviewRequiredForSO = false;
    this.isPrefabPanelApplied = false;
    this.isPrefabPanelNotApplied = false;
    this.isOvertimeEstimatedForProject = false;

    const context: IHierarchyIterationContext = {
      defaultEngOffice: deo,
      energySalesOffices: this.getAllowedEnergySalesOffices(),
      energyParts: this.getAllowedEnergyParts(),
      isPrefabAvailable: this.isPrefabAvailable(),
    };

    // Project-level labor tasks first, then recurse through every visible area.
    const laborData = await this.indexedDbService.getLaborDataByGuid(project.itemUniqueId);
    await this.processLevelTasks(laborData, context, false);

    for (const area of project.areas) {
      if (!area.isItemVisible) {
        continue;
      }
      await this.iterateArea(area, context);
    }

    // Item1: graphics standard verification is visible when graphics hours exceed the threshold.
    reviewObject.costReviewSummary.isGraphicsStandardVerifiedWithGISVisible =
      this.graphicsTaskValue > CONSTANTS.GRAPHICS_HOURS_VISIBILITY_THRESHOLD;

    // Item2: Connected Digital Package presence.
    reviewObject.costReviewSummary.isCDP = this.convertBoolToYesNo(this.cdpPartsQuantity > CONSTANTS.ZERO);

    // Item3: energy OM cost review required when a sales office OR a part triggers it.
    reviewObject.costReviewSummary.isEnergyOMReviewRequired = this.convertBoolToYesNo(
      this.isEnergyOMCostReviewRequiredForSO || this.omCostReviewPartsQuantity > CONSTANTS.ZERO
    );

    // Item4: prefab controller status (only stored on automation review attributes).
    const prefab = (reviewObject as CostReviewAttributes)?.costReviewPrefab;
    if (prefab) {
      prefab.prefabControllerUsed = this.getPrefabControllerUsed();
    }
  }

  private async iterateArea(area: SQWebArea, context: IHierarchyIterationContext): Promise<void> {
    let shouldEvaluateController = context.isPrefabAvailable &&
      (area.areaControllerState === ControllerState.HasSizedController ||
        area.areaControllerState === ControllerState.HasUnsizedController);
    // Area-level labor tasks.
    // context.laborsCollection[area.itemUniqueId]
    const laborData = await this.indexedDbService.getLaborDataByGuid(area.itemUniqueId);
    this.processLevelTasks(laborData, context, false);
  
    // Visible child components: recurse nested areas, process systems.
    for (const child of area.children ?? []) {
      if (child.nodeType === CONSTANTS.NODETYPE_CONTROLLER && shouldEvaluateController) {
        this.evaluatePrefabPanelState((child as SQWebController).prefabPanelApplied);
        shouldEvaluateController = false;
      } else if (child.nodeType === CONSTANTS.NODETYPE_AREA) {
        const nestedArea = child as unknown as SQWebArea;
        if (nestedArea.isItemVisible) {
          await this.iterateArea(nestedArea, context);
        }
      } else if (child.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
        const system = child as unknown as SQWebSystem;
        if (system.isItemVisible) {
          await this.processSystem(system, context);
        }
      }
    }
  }

  private async processSystem(system: SQWebSystem, context: IHierarchyIterationContext): Promise<void> {
    const levelId = this.commonFunctionsService.getCollectionHolderId(system);

    // System-level labor tasks (include DXR base tasks for DXR systems).
    const laborData = await this.indexedDbService.getLaborDataByGuid(levelId);
    this.processLevelTasks(laborData, context, system.isSystemDXR);

    // CDP / energy part quantities from the system's material tree.
    const materialData = await this.indexedDbService.getMaterialDataByGuid(levelId);
    this.calculateCdpAndOmParts(materialData?.systemChildrenCollection, CONSTANTS.ONE, context);
  }

  private  processLevelTasks(
    laborData: LaborData | undefined,
    context: IHierarchyIterationContext,
    includeDxrBaseTasks: boolean
  ): void {
    if (!laborData) {
      return;
    }

    for (const baseTask of laborData.baseTasksCollection ?? []) {
      this.checkValueForBaseAndAdjTask(baseTask, context);
    }

    if (includeDxrBaseTasks) {
      for (const dxrBaseTask of laborData.baseTasksDXRCollection ?? []) {
        this.checkValueForBaseAndAdjTask(dxrBaseTask, context);
      }
    }

    for (const task of laborData.tasksCollection ?? []) {
      this.checkValueForBaseAndAdjTask(task, context);
    }

    if (!this.isOvertimeEstimatedForProject && this.hasOvertimeApplied(laborData, includeDxrBaseTasks)) {
      this.isOvertimeEstimatedForProject = true;
    }
  }

  private checkValueForBaseAndAdjTask(task: Task, context: IHierarchyIterationContext): void {
    // Graphics labor aggregation: only graphics tasks for the default engineering office count.
    const isGraphicsTask =
      task.referenceNumber === CONSTANTS.WORKSTATION_GRAPHICS_FLOOR_PLANS_TASK_ID ||
      task.referenceNumber === CONSTANTS.DASHBOARD_GRAPHICS_TASK_ID ||
      task.referenceNumber === CONSTANTS.GRAPHICS_PREWORK_CLOSEOUT_TASK_ID;
    if (isGraphicsTask && task.salesOfficeId === context.defaultEngOffice) {
      this.graphicsTaskValue += this.normalizeLaborHours(task.graphicsLabor);
    }

    // Energy sales-office detection: flag when the task belongs to an OM-review sales office.
    if (context.energySalesOffices.has(String(task.salesOfficeId))) {
      this.isEnergyOMCostReviewRequiredForSO = true;
    }
  }

  private calculateCdpAndOmParts(
    components: SystemComponent[] | undefined,
    quantity: number,
    context: IHierarchyIterationContext
  ): void {
    for (const child of components ?? []) {
      if (child.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
        const subSystem = child as SubSystem;
        // Match desktop behavior: the subsystem quantity becomes the multiplier for its children.
        this.calculateCdpAndOmParts(subSystem.children, subSystem.quantity ?? CONSTANTS.ONE, context);
        continue;
      }

      const material = child as Material;
      if (!material.isMaster) {
        continue;
      }
      this.summateCdpPartsQuantity(material, quantity);
      this.summateOmReviewRequiredPartsQuantity(material, quantity, context.energyParts);
    }
  }

  private summateCdpPartsQuantity(material: Material, parentQuantity: number): void {
    const isCdpPart =
      material.referenceNumber === CONSTANTS.CXG3_X300_KIT ||
      material.referenceNumber === CONSTANTS.CXG3_X300_CONNECT_X300 ||
      material.referenceNumber === CONSTANTS.CXG3_X200_CONNECT_X200 ||
      material.referenceNumber === CONSTANTS.CFG3_F200_DESIGO_OPTIC;
    if (isCdpPart) {
      this.cdpPartsQuantity += (material.quantity ?? CONSTANTS.ZERO) * parentQuantity;
    }
  }

  private summateOmReviewRequiredPartsQuantity(
    material: Material,
    parentQuantity: number,
    energyParts: ReadonlySet<string>
  ): void {
    if (material.ssn && energyParts.has(material.ssn)) {
      this.omCostReviewPartsQuantity += (material.quantity ?? CONSTANTS.ZERO) * parentQuantity;
    }
  }

  private evaluatePrefabPanelState(prefabPanel: PrefabPanel): void {
    if (prefabPanel === PrefabPanel.None) {
      this.isPrefabPanelNotApplied = true;
    } else {
      this.isPrefabPanelApplied = true;
    }
  }

  private getPrefabControllerUsed(): string {
    if (this.isPrefabPanelApplied && this.isPrefabPanelNotApplied) {
      return CONSTANTS.MIXED;
    }
    if (this.isPrefabPanelApplied) {
      return CONSTANTS.YES;
    }
    if (this.isPrefabPanelNotApplied) {
      return CONSTANTS.NO;
    }
    return CONSTANTS.EMPTY_STRING;
  }

  private getAllowedEnergySalesOffices(): ReadonlySet<string> {
    return this.parseCommaSeparatedConfig(CONSTANTS.ALLOWED_ENERGY_SALES_OFFICES_FOR_COST_REVIEW_REQUIRED);
  }

  private getAllowedEnergyParts(): ReadonlySet<string> {
    return this.parseCommaSeparatedConfig(CONSTANTS.ALLOWED_ENERGY_PARTS_FOR_COST_REVIEW_REQUIRED);
  }

  private parseCommaSeparatedConfig(configKey: string): ReadonlySet<string> {
    const raw = this.commonFunctionsService.getGeneralConfigValue(configKey);
    if (!raw?.trim()) {
      return new Set<string>();
    }

    return new Set(
      raw.split(',')
        .map(value => value.trim())
        .filter(value => value.length > 0)
    );
  }

  private isPrefabAvailable(): boolean {
    const cultureId = this.commonDataService.getEstimateCultureId();
    const configKey = cultureId === CULTURE_ID.EN_US
      ? CONSTANTS.IS_PREFAB_AVAILABLE
      : CONSTANTS.IS_PREFAB_AVAILABLE_CA;

    const raw = this.commonFunctionsService.getGeneralConfigValue(configKey);
    return raw?.trim().toLowerCase() === 'true';
  }

  private normalizeLaborHours(value: number | null | undefined): number {
    if (value == null || CONSTANTS.SENTINEL_LABOR_VALUES.includes(value)) {
      return CONSTANTS.ZERO;
    }
    return value;
  }

  private syncLaborDataToPlannedCosts(reviewObject: CostReviewBaseAttributes): void {
    const laborCosts = this.solEstimateService?.estimateModel?.laborCosts;
    const plannedCosts = reviewObject?.costReviewPlannedCosts;
    const business = this.commonDataService.getBusiness();

    if (!plannedCosts?.length || !laborCosts) {
      return;
    }

    for (const plannedCost of plannedCosts) {
      const mapping = this.plannedCostLaborMappings.find(item => item.itemName === plannedCost.itemName);
      if (!mapping) {
        continue;
      }

      const rowGroup = (laborCosts as Record<string, any>)[mapping.laborFormKey] as { tdcPercentage?: number; cost?: number } | undefined;
      plannedCost.tdcPercent = rowGroup?.tdcPercentage ?? CONSTANTS.ZERO;
      plannedCost.itemCost = rowGroup?.cost ?? CONSTANTS.ZERO;
    }

    const engineeringCost = plannedCosts.find(cost => cost.itemName === CostReviewPlannedCostItemName.engineering);
    if ((business === CONSTANTS.BUSINESS_AUTOMATION || business === CONSTANTS.BUSINESS_MECHANICAL) && engineeringCost) {
      const engineerTarget = this.getMinEngineerPercentForAutomation();
      if (engineerTarget !== null) {
        engineeringCost.target = engineerTarget;
        reviewObject.isEngineerExceptionApplicable = engineeringCost.tdcPercent < engineeringCost.target;
      }
    }

  }

  private initializePlannedCost(reviewObject: CostReviewBaseAttributes): void {

    reviewObject.isNationalHistoricalRowVisible = this.commonDataService.getPreferenceData().preferences?.cultureString === CONSTANTS.LANGUAGE_EN_US;

    const { withLabors, withoutLabors } = this.loadPlannedCostDetails(reviewObject);

    reviewObject.costTotal = withLabors.reduce((total, cost) => total + (cost.itemCost ?? CONSTANTS.ZERO), CONSTANTS.ZERO);
    reviewObject.tdcTotal = withLabors.reduce((total, cost) => total + (cost.tdcPercent ?? CONSTANTS.ZERO), CONSTANTS.ZERO);
    reviewObject.itemCostTotal = withoutLabors.reduce((total, cost) => total + (cost.itemCost ?? CONSTANTS.ZERO), CONSTANTS.ZERO);
    reviewObject.itemTDCTotal = withoutLabors.reduce((total, cost) => total + (cost.tdcPercent ?? CONSTANTS.ZERO), CONSTANTS.ZERO);
  }

  private loadPlannedCostDetails(reviewObject: CostReviewBaseAttributes): IPlannedCostCollections {
    this.initializeCostReviewPlannedCosts(reviewObject);
    return this.generatePlannedCostCollections(reviewObject.costReviewPlannedCosts ?? []);
  }

  private initializeCostReviewPlannedCosts(reviewObject: CostReviewBaseAttributes): void {
    this.syncLaborDataToPlannedCosts(reviewObject);
    this.syncItemDataToCostReview(reviewObject);

    const estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab as unknown as Record<string, number> | undefined;
    const plannedCosts = reviewObject.costReviewPlannedCosts ?? [];
    for (const plannedCost of plannedCosts) {
      const mapping = this.plannedCostLaborMappings.find(item => item.itemName === plannedCost.itemName);
      if (mapping && estimateTab) {
        plannedCost.hours = estimateTab[mapping.estimateTabKey] ?? CONSTANTS.ZERO;
      }
    }
  }

  private generatePlannedCostCollections(plannedCosts: CostReviewPlannedCosts[]): IPlannedCostCollections {
    const sortByPositionId = (left: CostReviewPlannedCosts, right: CostReviewPlannedCosts): number =>
      (left.positionId ?? CONSTANTS.ZERO) - (right.positionId ?? CONSTANTS.ZERO);

    const withLabors = plannedCosts
      .filter(plannedCost => {
        if (!plannedCost) {
          return false;
        }
        const positionId = String(plannedCost.positionId ?? '');
        return positionId.startsWith('2') || positionId.startsWith('3');
      })
      .sort(sortByPositionId);

    const withoutLabors = plannedCosts
      .filter(plannedCost => plannedCost != null && String(plannedCost.positionId ?? '').startsWith('1'))
      .sort(sortByPositionId);

    return { withLabors, withoutLabors };
  }

  private updatePrefabControllerVisibility(costReviewData: CostReviewDatabaseRecord): void {
    if(this.business === CONSTANTS.BUSINESS_AUTOMATION || this.business === CONSTANTS.BUSINESS_MECHANICAL) {

      const costReviewPrefab = (costReviewData.reviewObject as CostReviewAttributes).costReviewPrefab;
      costReviewPrefab.isPrefabControllerVisible = 
        (costReviewData.newReviewObject.costReviewType === CostReviewType.AutomationEnUs || 
          costReviewData.newReviewObject.costReviewType === CostReviewType.MechanicalEnUs) 
        && (!!costReviewPrefab.prefabControllerUsed); 
    }
    
  }

  private updateMarginDetails(reviewObject: CostReviewBaseAttributes): void {
    const segment = this.commonDataService.getEstimateData()?.profitCenterId ?? 0;
    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    const isMarkUpSelected = this.solEstimateService.estimateModel?.markUpToggle ?? estimateMetaData?.estimateTab?.isMarkUpSelected ?? false;
    const reportedSellAs = this.solEstimateService.estimateModel?.sellAs?.reported;
    const minimumGrossMargin = this.sellAsMinimumMarginService.SellAsMinimumMarginValue;

    const escalationTotal = this.commonDataService.getRootNodeHidden()
      ? CONSTANTS.ZERO
      : this.estimateCommonFunctionsService.calculateEscalationTotal(estimateMetaData?.escalationRates);
    reviewObject.costReviewMarginExceptions.escalationCost = escalationTotal;
    reviewObject.costReviewMarginExceptions.escalationApplied = this.convertBoolToYesNo(escalationTotal > CONSTANTS.ZERO);

    reviewObject.costReviewMarginExceptions.estimatedMargin = isMarkUpSelected
      ? reportedSellAs?.markUpPercentage ?? CONSTANTS.ZERO
      : reportedSellAs?.grossMarginPercentage ?? CONSTANTS.ZERO;
    reviewObject.costReviewMarginExceptions.estimatedMarginMinimum = isMarkUpSelected
      ? this.estimateCommonFunctionsService.getMarkUpPercent(minimumGrossMargin)
      : minimumGrossMargin;
    reviewObject.costReviewMarginExceptions.estimatedMarginVariance = reviewObject.costReviewMarginExceptions.estimatedMargin - reviewObject.costReviewMarginExceptions.estimatedMarginMinimum;

    this.handleCostReviewType(reviewObject, segment);

    const estimatedMarginMinimum = reviewObject.costReviewMarginExceptions.estimatedMarginMinimum;
    const estimatedMarginVariance = reviewObject.costReviewMarginExceptions.estimatedMarginVariance;
    const isSalesTypeVisible = this.commonDataService.getEstimateMetaData()?.estimateSettings?.isSalesTypeVisible ?? false;
    reviewObject.costReviewMarginExceptions.isEstimateClassificationApplicable = this.isServiceProject && !isSalesTypeVisible 
    && (estimatedMarginMinimum !== CONSTANTS.ZERO);

    reviewObject.costReviewMarginExceptions.isReasonForExceptionApplicable = this.isServiceProject &&
      (reviewObject.costReviewMarginExceptions.estimatedMarginMinimum !== 0) &&
      (estimatedMarginVariance < 0);

    if (!reviewObject.costReviewMarginExceptions.isReasonForExceptionApplicable &&
      (
        reviewObject.costReviewMarginExceptions.isAutomationExceptionVisible ||
        reviewObject.costReviewMarginExceptions.isElectricalExceptionVisible ||
        reviewObject.costReviewMarginExceptions.isFireExceptionVisible ||
        reviewObject.costReviewMarginExceptions.isSecurityExceptionVisible ||
        reviewObject.costReviewMarginExceptions.isMechanicalExceptionVisible
      )
    ) {
      reviewObject.costReviewMarginExceptions.reasonForException = '';
      reviewObject.costReviewMarginExceptions.smOrBgmApprovalRecieved = CONSTANTS.TWO;
      reviewObject.costReviewMarginExceptions.approverEmailId = '';
    }
  }

  private handleCostReviewType(reviewObject: CostReviewBaseAttributes, segment: number): void {
    const costReviewType = this.commonDataService.getEstimateOpenResponse()?.costReviewDatabaseRecord?.newReviewObject?.costReviewType;

    switch (costReviewType) {
      case CostReviewType.AutomationEnUs:
        this.updateMarginDetailsForAutomation(reviewObject, segment);
        break;
      case CostReviewType.SecurityEnUs:
        this.updateMarginDetailsForSecurity(reviewObject, segment);
        this.updateReasonForLaborVariance(reviewObject as CostReviewSecurityEnUsAttributes);
        break;
      case CostReviewType.FireEnUs:
        this.updateMarginDetailsForFire(reviewObject, segment);
        break;
      case CostReviewType.ElectricalEnUs:
        this.updateMarginDetailsForElectrical(reviewObject, segment);
        break;
      case CostReviewType.MechanicalEnUs:
        this.updateMarginDetailsForMechanical(reviewObject, segment);
        break;
    }
  }

  private updateMarginDetailsForAutomation(reviewObject: CostReviewBaseAttributes, segment: number): void {
    reviewObject.costReviewMarginExceptions.isAutomationExceptionVisible = true;
    reviewObject.costReviewMarginExceptions.isElectricalExceptionVisible = false;

    this.isServiceProject = segment === CONSTANTS.TWO ||
      segment === CONSTANTS.TWELVE ||
      this.commonFunctionsService.isBPSEnergyServicesSegmentId(segment) ||
      this.commonFunctionsService.isBPSEnergyServicesIoTSegmentID(segment) ||
      segment === CONSTANTS.FIFTY ||
      segment === CONSTANTS.FIFTY_ONE ||
      segment === CONSTANTS.ELEVEN;
  }

  private updateMarginDetailsForSecurity(reviewObject: CostReviewBaseAttributes, segment: number): void {
    reviewObject.costReviewMarginExceptions.isSecurityExceptionVisible = true;

    this.isServiceProject = segment === CONSTANTS.TWENTY_SEVEN ||
      segment === CONSTANTS.TWENTY_EIGHT ||
      segment === CONSTANTS.TWENTY_FOUR ||
      segment === CONSTANTS.FIFTY_ONE;
  }

  private updateMarginDetailsForFire(reviewObject: CostReviewBaseAttributes, segment: number): void {
    reviewObject.costReviewMarginExceptions.isFireExceptionVisible = true;

    this.isServiceProject = segment === CONSTANTS.TWENTY_FIVE ||
      segment === CONSTANTS.TWENTY_SIX ||
      segment === CONSTANTS.FIFTY_ONE ||
      segment === CONSTANTS.TWENTY_TWO;
  }

  private updateMarginDetailsForElectrical(reviewObject: CostReviewBaseAttributes, segment: number): void {
    reviewObject.costReviewMarginExceptions.isElectricalExceptionVisible = true;
    reviewObject.costReviewMarginExceptions.isAutomationExceptionVisible = false;

    this.isServiceProject = segment === CONSTANTS.FORTY_EIGHT ||
      segment === CONSTANTS.FORTY_NINE;
  }

  private updateMarginDetailsForMechanical(reviewObject: CostReviewBaseAttributes, segment: number): void {
    reviewObject.costReviewMarginExceptions.isMechanicalExceptionVisible = true;
    reviewObject.costReviewMarginExceptions.isAutomationExceptionVisible = false;

    this.isServiceProject = segment === CONSTANTS.TWO ||
      segment === CONSTANTS.TWELVE ||
      this.commonFunctionsService.isBPSEnergyServicesSegmentId(segment) ||
      this.commonFunctionsService.isBPSEnergyServicesIoTSegmentID(segment) ||
      segment === CONSTANTS.FIFTY ||
      segment === CONSTANTS.FIFTY_ONE ||
      segment === CONSTANTS.ELEVEN;
  }

  private updateReasonForLaborVariance(reviewObject: CostReviewSecurityEnUsAttributes | null): void {
    if (this.commonDataService.getBusiness() !== CONSTANTS.BUSINESS_SECURITY) {
      return;
    }

    const threshold = this.getSecurityLaborTdcThresholdFromGeneralConfigs();
    const laborTdcPercent = Number(reviewObject?.tdcTotal ?? CONSTANTS.ZERO);
    if (threshold === null || Number.isNaN(laborTdcPercent)) {
      reviewObject.isLaborTDCPercentExceeds = false;
      return;
    }

    reviewObject.isLaborTDCPercentExceeds = laborTdcPercent > threshold;
    if (!reviewObject.isLaborTDCPercentExceeds) {
      reviewObject.reasonForLaborVariance = CONSTANTS.EMPTY_STRING;
    }
  }

  private getSecurityLaborTdcThresholdFromGeneralConfigs(): number | null {
    const cultureId = this.commonDataService.getEstimateCultureId();
    const configKey = cultureId === CULTURE_ID.EN_US
      ? CONSTANTS.COST_REVIEW_LABOR_TDC_PERCENT_THRESHOLD_US
      : CONSTANTS.COST_REVIEW_LABOR_TDC_PERCENT_THRESHOLD_CA;

    const raw = this.commonFunctionsService.getGeneralConfigValue(configKey);
    if (!raw?.trim()) {
      return null;
    }

    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private getMinEngineerPercentForAutomation(): number | null {
    const cultureId = this.commonDataService.getEstimateCultureId();
    const configKey = cultureId === CULTURE_ID.EN_US
      ? CONSTANTS.MIN_ENGINEER_PERCENT_FOR_AUTOMATION
      : CONSTANTS.MIN_ENGINEER_PERCENT_FOR_AUTOMATION_CA;

    const raw = this.commonFunctionsService.getGeneralConfigValue(configKey);
    if (!raw?.trim()) {
      return null;
    }

    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private getFormValue(source: any, ...path: string[]): number {
    let current = source;
    for (const key of path) {
      if (current == null) {
        return CONSTANTS.ZERO;
      }

      if (typeof current === 'object' && 'get' in current && typeof (current as any).get === 'function') {
        current = (current as any).get(key);
      } else {
        current = current[key];
      }
    }

    return Number(current ?? CONSTANTS.ZERO);
  }

  private sumMaterialFieldValues(materialCosts: any, subField: string, fieldName: string): number {
    const materialTypes = ['siemensMaterial', 'outsideMaterial', 'mechanicalMaterial', 'electricalMaterial'];
    return materialTypes.reduce((sum, materialType) => {
      return sum + this.getFormValue(materialCosts, materialType, subField, fieldName);
    }, CONSTANTS.ZERO);
  }

  private syncItemDataToCostReview(reviewObject: CostReviewBaseAttributes): void {
    const plannedCosts = reviewObject?.costReviewPlannedCosts;

    if (!plannedCosts?.length) {
      return;
    }

    for (const plannedCost of plannedCosts) {
      switch (plannedCost.itemName) {
        case CONSTANTS.PLANNED_COST_ITEM_NAMES.risk:
          this.updateRiskCostItem(plannedCost);
          break;
        case CONSTANTS.PLANNED_COST_ITEM_NAMES.material:
          this.updateMaterialCostItem(plannedCost);
          break;
        case CONSTANTS.PLANNED_COST_ITEM_NAMES.subcontractor:
          this.updateSubcontractorCostItem(plannedCost);
          break;
        case CONSTANTS.PLANNED_COST_ITEM_NAMES.miscellaneous:
          this.updateMiscellaneousCostItem(plannedCost);
          break;
        case CONSTANTS.PLANNED_COST_ITEM_NAMES.shippingAndHandling:
          this.updateShippingHandlingCostItem(plannedCost);
          break;
        case CONSTANTS.PLANNED_COST_ITEM_NAMES.warranty:
          this.updateWarrantyCostItem(plannedCost);
          break;
        case CONSTANTS.PLANNED_COST_ITEM_NAMES.other:
          break;
      }
    }

    const otherCost = plannedCosts.find(cost => cost.itemName === CONSTANTS.PLANNED_COST_ITEM_NAMES.other);
    if (otherCost) {
      this.updateOtherCostItem(otherCost, plannedCosts);
    }
  }

  private updateRiskCostItem(plannedCost: CostReviewPlannedCosts): void {
    const riskCosts = this.solEstimateService.estimateModel?.miscRiskTravelCosts?.riskCosts;
    if (riskCosts) {
      plannedCost.tdcPercent = this.getFormValue(riskCosts, 'headerRow', 'tdcPercentage');
      plannedCost.itemCost = this.getFormValue(riskCosts, 'headerRow', 'cost');
    }
  }

  private updateMaterialCostItem(plannedCost: CostReviewPlannedCosts): void {
    const materialCosts = this.solEstimateService.estimateModel?.materialCosts;
    if (materialCosts) {
      plannedCost.tdcPercent = this.sumMaterialFieldValues(materialCosts, 'headerRow', 'tdcPercentage');
      plannedCost.itemCost = this.sumMaterialFieldValues(materialCosts, 'headerRow', 'cost');
    }
  }

  private updateSubcontractorCostItem(plannedCost: CostReviewPlannedCosts): void {
    const subContractorCosts = this.solEstimateService.estimateModel?.subContractorCosts;
    plannedCost.tdcPercent = this.getFormValue(subContractorCosts, 'headerRow', 'tdcPercentage');
    plannedCost.itemCost = this.getFormValue(subContractorCosts, 'headerRow', 'cost');
  }

  private updateMiscellaneousCostItem(plannedCost: CostReviewPlannedCosts): void {
    const miscRows = this.solEstimateService.estimateModel?.miscRiskTravelCosts?.rows ?? [];
    const miscCostNames = ['MISCELLANEOUS_COSTS', 'MECHANICAL_FEE', 'SUBCONTRACTOR_FEE', 'PRODUCTIVITY_RESERVE'];
    const miscCostRows = miscRows.filter(row => miscCostNames.includes(row.name));
    plannedCost.tdcPercent = miscCostRows.reduce((total, row) => total + this.getFormValue(row, 'tdcPercentage'), CONSTANTS.ZERO);
    plannedCost.itemCost = miscCostRows.reduce((total, row) => total + this.getFormValue(row, 'cost'), CONSTANTS.ZERO);
  }

  private updateShippingHandlingCostItem(plannedCost: CostReviewPlannedCosts): void {
    const materialCosts = this.solEstimateService.estimateModel?.materialCosts;
    if (materialCosts) {
      plannedCost.tdcPercent = this.sumMaterialFieldValues(materialCosts, 'shippingAndHandling', 'tdcPercentage');
      plannedCost.itemCost = this.sumMaterialFieldValues(materialCosts, 'shippingAndHandling', 'cost');
    }
  }

  private updateWarrantyCostItem(plannedCost: CostReviewPlannedCosts): void {
    const materialCosts = this.solEstimateService.estimateModel?.materialCosts;
    if (materialCosts) {
      plannedCost.tdcPercent = this.sumMaterialFieldValues(materialCosts, 'warranty', 'tdcPercentage');
      plannedCost.itemCost = this.sumMaterialFieldValues(materialCosts, 'warranty', 'cost');
    }
  }

  private updateOtherCostItem(plannedCost: CostReviewPlannedCosts, plannedCosts: CostReviewPlannedCosts[]): void {
    const subTotals = this.solEstimateService.estimateModel?.subTotals;
    if (!subTotals) {
      return;
    }

    const costsExceptOther = plannedCosts.filter(cost => cost !== plannedCost);
    plannedCost.itemCost = subTotals.cost - costsExceptOther.reduce((total, cost) => total + (cost.itemCost ?? CONSTANTS.ZERO), CONSTANTS.ZERO);
    plannedCost.tdcPercent = subTotals.tdcPercentage - costsExceptOther.reduce((total, cost) => total + (cost.tdcPercent ?? CONSTANTS.ZERO), CONSTANTS.ZERO);
  }

  private getshipToLocation(shipToAccount: CustomerInformation): string {
    if (shipToAccount) {
      return [
        shipToAccount.customerName,
        shipToAccount.custAddress1,
        shipToAccount.custAddress2,
        shipToAccount.custCity,
        shipToAccount.custState,
        shipToAccount.custZipCode,
      ]
        .filter(value => !!value?.trim())
        .join(CONSTANTS.SPACE);
    }
    return '';
  }
}



